import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box,
  Typography, CircularProgress
} from '@mui/material';
import { Close } from '@mui/icons-material';

export default function FilePreview({ aberto, arquivo, onClose }) {
  if (!arquivo) return null;

  const { nome, url, tipo } = arquivo;
  const extensao = nome.split('.').pop().toLowerCase();

  const isImagem = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(extensao);
  const isPdf = tipo?.includes('pdf') || extensao === 'pdf';
  const isExcel = ['xlsx', 'xls', 'csv'].includes(extensao);
  const isDoc = ['doc', 'docx'].includes(extensao);
  const isText = ['txt', 'json', 'xml', 'html', 'css', 'js'].includes(extensao);
  const isVideo = ['mp4', 'avi', 'mov', 'mkv', 'webm'].includes(extensao);
  const isAudio = ['mp3', 'wav', 'ogg', 'm4a', 'flac'].includes(extensao);

  return (
    <Dialog open={aberto} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, wordBreak: 'break-word', flex: 1 }}>
          {nome}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {/* Imagens */}
        {isImagem && (
          <Box sx={{ width: '100%', textAlign: 'center' }}>
            <img src={url} alt={nome} style={{ maxWidth: '100%', maxHeight: '500px', borderRadius: '8px' }} />
          </Box>
        )}

        {/* PDF */}
        {isPdf && (
          <iframe
            src={`${url}#toolbar=1&navpanes=0&scrollbar=1`}
            style={{ width: '100%', height: '500px', border: 'none', borderRadius: '8px' }}
            title={nome}
          />
        )}

        {/* Video */}
        {isVideo && (
          <video width="100%" height="400" controls style={{ borderRadius: '8px' }}>
            <source src={url} type={tipo} />
            Seu navegador não suporta vídeo.
          </video>
        )}

        {/* Audio */}
        {isAudio && (
          <Box sx={{ width: '100%', textAlign: 'center' }}>
            <audio width="100%" controls style={{ marginBottom: '20px' }}>
              <source src={url} type={tipo} />
              Seu navegador não suporta áudio.
            </audio>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
              Clique no player acima para ouvir
            </Typography>
          </Box>
        )}

        {/* Excel/CSV */}
        {isExcel && (
          <Box sx={{ textAlign: 'center', p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>📊 Arquivo {extensao.toUpperCase()}</Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Não é possível visualizar {extensao.toUpperCase()} no navegador.
            </Typography>
            <Button
              variant="contained"
              onClick={() => window.open(url, '_blank')}
              sx={{ backgroundColor: '#FF8C00', '&:hover': { backgroundColor: '#E67E00' } }}
            >
              💾 Download e Abrir
            </Button>
          </Box>
        )}

        {/* Word */}
        {isDoc && (
          <Box sx={{ textAlign: 'center', p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>📄 Documento Word</Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Não é possível visualizar {extensao.toUpperCase()} no navegador.
            </Typography>
            <Button
              variant="contained"
              onClick={() => window.open(`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`, '_blank')}
              sx={{ backgroundColor: '#FF8C00', '&:hover': { backgroundColor: '#E67E00' } }}
            >
              👁️ Visualizar Online
            </Button>
            <Button
              variant="outlined"
              onClick={() => window.open(url, '_blank')}
              sx={{ ml: 1, borderColor: '#FF8C00', color: '#FF8C00' }}
            >
              💾 Download
            </Button>
          </Box>
        )}

        {/* Texto */}
        {isText && (
          <TextFilePreview url={url} nome={nome} />
        )}

        {/* Sem suporte */}
        {!isImagem && !isPdf && !isExcel && !isDoc && !isVideo && !isAudio && !isText && (
          <Box sx={{ textAlign: 'center', p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>📦 Arquivo {extensao.toUpperCase()}</Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Visualização não suportada para este formato.
            </Typography>
            <Button
              variant="contained"
              onClick={() => window.open(url, '_blank')}
              sx={{ backgroundColor: '#FF8C00', '&:hover': { backgroundColor: '#E67E00' } }}
            >
              💾 Download
            </Button>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Fechar</Button>
        <Button
          variant="contained"
          onClick={() => window.open(url, '_blank')}
          sx={{ backgroundColor: '#FF8C00', '&:hover': { backgroundColor: '#E67E00' } }}
        >
          📥 Download
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function TextFilePreview({ url, nome }) {
  const [conteudo, setConteudo] = React.useState('');
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    fetch(url)
      .then(r => r.text())
      .then(text => {
        setConteudo(text.substring(0, 5000)); // Primeiras 5000 caracteres
        setLoading(false);
      })
      .catch(() => {
        setConteudo('Não foi possível carregar o arquivo.');
        setLoading(false);
      });
  }, [url]);

  return (
    <Box sx={{ width: '100%', maxHeight: '400px', overflow: 'auto' }}>
      {loading ? (
        <CircularProgress />
      ) : (
        <pre style={{
          backgroundColor: '#f5f5f5',
          padding: '15px',
          borderRadius: '8px',
          fontFamily: 'monospace',
          fontSize: '12px',
          whiteSpace: 'pre-wrap',
          wordWrap: 'break-word'
        }}>
          {conteudo}
          {conteudo.length === 5000 && '\n\n... (arquivo truncado)'}
        </pre>
      )}
    </Box>
  );
}
